import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Schedule,
  Person,
  CalendarToday,
  AccessTime,
  VideoCall,
  LocationOn,
  Add,
  Check,
  Close,
  Edit,
  Delete,
  Refresh,
} from '@mui/icons-material';

interface Teacher {
  id: number;
  name: string;
  subject: string;
  email: string;
  avatar?: string;
  availableSlots: TimeSlot[];
}

interface TimeSlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  duration: number; // in minutes
  type: 'in-person' | 'virtual';
  location?: string;
  available: boolean;
}

interface Meeting {
  id: number;
  teacherId: number;
  teacherName: string;
  childId: number;
  childName: string;
  date: string;
  startTime: string;
  endTime: string;
  type: 'in-person' | 'virtual';
  location?: string;
  meetingLink?: string;
  topic: string;
  notes?: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  createdAt: string;
}

interface Child {
  id: number;
  name: string;
  grade: string;
  teachers: number[];
}

const ParentMeetingScheduler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [selectedChild, setSelectedChild] = useState<number | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [meetingTopic, setMeetingTopic] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [bookingMeeting, setBookingMeeting] = useState(false);

  // Pre-populate if coming from a specific context
  const preSelectedTeacherId = searchParams.get('teacherId');
  const preSelectedChildId = searchParams.get('childId');

  const steps = ['Select Child & Teacher', 'Choose Time Slot', 'Meeting Details', 'Confirm Booking'];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockChildren: Child[] = [
        { id: 1, name: 'Emma Johnson', grade: '6th Grade', teachers: [1, 2, 3] },
        { id: 2, name: 'Lucas Johnson', grade: '3rd Grade', teachers: [4, 5, 6] },
      ];

      const mockTeachers: Teacher[] = [
        { 
          id: 1, 
          name: 'Ms. Sarah Wilson', 
          subject: 'Mathematics', 
          email: 'sarah.wilson@school.edu',
          availableSlots: [
            {
              id: 1,
              date: '2024-01-15',
              startTime: '14:00',
              endTime: '14:30',
              duration: 30,
              type: 'in-person',
              location: 'Room 205',
              available: true
            },
            {
              id: 2,
              date: '2024-01-15',
              startTime: '15:00',
              endTime: '15:45',
              duration: 45,
              type: 'virtual',
              available: true
            },
            {
              id: 3,
              date: '2024-01-16',
              startTime: '13:30',
              endTime: '14:00',
              duration: 30,
              type: 'in-person',
              location: 'Room 205',
              available: true
            }
          ]
        },
        { 
          id: 2, 
          name: 'Mr. David Brown', 
          subject: 'English Literature', 
          email: 'david.brown@school.edu',
          availableSlots: [
            {
              id: 4,
              date: '2024-01-17',
              startTime: '16:00',
              endTime: '16:30',
              duration: 30,
              type: 'virtual',
              available: true
            }
          ]
        }
      ];

      const mockMeetings: Meeting[] = [
        {
          id: 1,
          teacherId: 1,
          teacherName: 'Ms. Sarah Wilson',
          childId: 1,
          childName: 'Emma Johnson',
          date: '2024-01-12',
          startTime: '14:00',
          endTime: '14:30',
          type: 'in-person',
          location: 'Room 205',
          topic: 'Mathematics Progress Discussion',
          status: 'completed',
          createdAt: '2024-01-10T10:00:00Z'
        },
        {
          id: 2,
          teacherId: 2,
          teacherName: 'Mr. David Brown',
          childId: 1,
          childName: 'Emma Johnson',
          date: '2024-01-20',
          startTime: '15:30',
          endTime: '16:00',
          type: 'virtual',
          meetingLink: 'https://meet.google.com/abc-def-ghi',
          topic: 'English Literature Discussion',
          status: 'confirmed',
          createdAt: '2024-01-15T09:00:00Z'
        }
      ];

      setChildren(mockChildren);
      setTeachers(mockTeachers);
      setMeetings(mockMeetings);

      // Pre-populate based on URL parameters
      if (preSelectedChildId) {
        setSelectedChild(parseInt(preSelectedChildId));
      }
      if (preSelectedTeacherId) {
        setSelectedTeacher(parseInt(preSelectedTeacherId));
      }

      setLoading(false);
    }, 1000);
  }, [preSelectedTeacherId, preSelectedChildId]);

  const handleNext = () => {
    if (activeStep === 0 && (!selectedChild || !selectedTeacher)) {
      return;
    }
    if (activeStep === 1 && !selectedSlot) {
      return;
    }
    if (activeStep === 2 && !meetingTopic.trim()) {
      return;
    }
    
    if (activeStep === steps.length - 1) {
      setConfirmDialogOpen(true);
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleBookMeeting = async () => {
    if (!selectedChild || !selectedTeacher || !selectedSlot || !meetingTopic.trim()) {
      return;
    }

    setBookingMeeting(true);
    
    // Mock booking - replace with API call
    setTimeout(() => {
      const newMeeting: Meeting = {
        id: meetings.length + 1,
        teacherId: selectedTeacher,
        teacherName: teachers.find(t => t.id === selectedTeacher)?.name || '',
        childId: selectedChild,
        childName: children.find(c => c.id === selectedChild)?.name || '',
        date: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        type: selectedSlot.type,
        location: selectedSlot.location,
        meetingLink: selectedSlot.type === 'virtual' ? 'https://meet.google.com/new-meeting' : undefined,
        topic: meetingTopic,
        notes: meetingNotes,
        status: 'pending',
        createdAt: new Date().toISOString()
      };

      setMeetings(prev => [...prev, newMeeting]);
      setConfirmDialogOpen(false);
      setBookingMeeting(false);
      
      // Navigate to meetings page
      navigate('/parent-messages?tab=1');
    }, 2000);
  };

  const getAvailableTeachers = () => {
    if (!selectedChild) return [];
    
    const child = children.find(c => c.id === selectedChild);
    if (!child) return [];
    
    return teachers.filter(t => child.teachers.includes(t.id));
  };

  const getAvailableSlots = () => {
    if (!selectedTeacher) return [];
    
    const teacher = teachers.find(t => t.id === selectedTeacher);
    return teacher?.availableSlots.filter(slot => slot.available) || [];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2024-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-messages')}
            sx={{ cursor: 'pointer' }}
          >
            Messages
          </Link>
          <Typography color="text.primary">Schedule Meeting</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-messages')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Schedule Meeting
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Book a meeting with your child's teacher
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Existing Meetings */}
      {meetings.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Your Scheduled Meetings
            </Typography>
            <List sx={{ p: 0 }}>
              {meetings.slice(-3).map((meeting, index) => (
                <React.Fragment key={meeting.id}>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {meeting.teacherName.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {meeting.teacherName} • {meeting.childName}
                          </Typography>
                          <Chip 
                            label={meeting.status} 
                            color={
                              meeting.status === 'confirmed' ? 'success' : 
                              meeting.status === 'pending' ? 'warning' : 
                              meeting.status === 'completed' ? 'info' : 'default'
                            }
                            size="small" 
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {meeting.topic}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(meeting.date)} at {formatTime(meeting.startTime)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {meeting.type === 'virtual' ? (
                              <VideoCall sx={{ fontSize: 16, color: 'primary.main' }} />
                            ) : (
                              <LocationOn sx={{ fontSize: 16, color: 'secondary.main' }} />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {meeting.type === 'virtual' ? 'Virtual Meeting' : meeting.location}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < meetings.slice(-3).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Child and Teacher
              </Typography>
              
              {/* Child Selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Child</InputLabel>
                <Select
                  value={selectedChild || ''}
                  label="Select Child"
                  onChange={(e) => {
                    setSelectedChild(e.target.value as number);
                    setSelectedTeacher(null); // Reset teacher selection
                  }}
                >
                  {children.map((child) => (
                    <MenuItem key={child.id} value={child.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {child.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1">{child.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {child.grade}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Teacher Selection */}
              <FormControl fullWidth disabled={!selectedChild}>
                <InputLabel>Select Teacher</InputLabel>
                <Select
                  value={selectedTeacher || ''}
                  label="Select Teacher"
                  onChange={(e) => setSelectedTeacher(e.target.value as number)}
                >
                  {getAvailableTeachers().map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                          {teacher.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1">{teacher.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {teacher.subject} • {teacher.availableSlots.length} slots available
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Choose Available Time Slot
              </Typography>
              
              {getAvailableSlots().length === 0 ? (
                <Alert severity="info">
                  No available time slots for this teacher. Please try selecting a different teacher or check back later.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {getAvailableSlots().map((slot) => (
                    <Grid item xs={12} sm={6} key={slot.id}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: selectedSlot?.id === slot.id ? '2px solid' : '1px solid',
                          borderColor: selectedSlot?.id === slot.id ? 'primary.main' : 'divider',
                          bgcolor: selectedSlot?.id === slot.id ? 'primary.50' : 'background.paper',
                          '&:hover': { 
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50' 
                          }
                        }}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {formatDate(slot.date)}
                          </Typography>
                          <Chip 
                            label={slot.type} 
                            color={slot.type === 'virtual' ? 'primary' : 'secondary'}
                            size="small" 
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({slot.duration} min)
                          </Typography>
                        </Box>
                        {slot.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {slot.location}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Meeting Details
              </Typography>
              
              <TextField
                label="Meeting Topic"
                placeholder="What would you like to discuss?"
                fullWidth
                required
                value={meetingTopic}
                onChange={(e) => setMeetingTopic(e.target.value)}
                sx={{ mb: 3 }}
              />

              <TextField
                label="Additional Notes (Optional)"
                placeholder="Any specific points you'd like to cover?"
                multiline
                rows={4}
                fullWidth
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
              />
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirm Meeting Details
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review your meeting details before confirming. The teacher will receive a notification and can confirm or suggest alternative times.
              </Alert>

              <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Teacher
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {teachers.find(t => t.id === selectedTeacher)?.name}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      Child
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {children.find(c => c.id === selectedChild)?.name}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {selectedSlot && formatDate(selectedSlot.date)}
                      <br />
                      {selectedSlot && formatTime(selectedSlot.startTime)} - {selectedSlot && formatTime(selectedSlot.endTime)}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">
                      Meeting Type
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {selectedSlot?.type === 'virtual' ? (
                        <VideoCall sx={{ fontSize: 16, color: 'primary.main' }} />
                      ) : (
                        <LocationOn sx={{ fontSize: 16, color: 'secondary.main' }} />
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedSlot?.type === 'virtual' ? 'Virtual Meeting' : selectedSlot?.location}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Topic
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {meetingTopic}
                    </Typography>
                    
                    {meetingNotes && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">
                          {meetingNotes}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button 
              onClick={handleBack} 
              disabled={activeStep === 0}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={
                (activeStep === 0 && (!selectedChild || !selectedTeacher)) ||
                (activeStep === 1 && !selectedSlot) ||
                (activeStep === 2 && !meetingTopic.trim())
              }
            >
              {activeStep === steps.length - 1 ? 'Book Meeting' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Meeting Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to book this meeting? The teacher will be notified and can confirm or suggest alternative times.
          </Typography>
          <Alert severity="info">
            You will receive an email confirmation once the teacher responds to your request.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleBookMeeting}
            disabled={bookingMeeting}
            startIcon={bookingMeeting ? <CircularProgress size={16} /> : <Check />}
          >
            {bookingMeeting ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ParentMeetingScheduler;
